package com.milta.feature.aicredit.data.remote.dto

import com.milta.feature.admin.data.remote.dto.AdminAccountDto
import com.milta.feature.admin.data.remote.dto.toDomain
import com.milta.feature.aicredit.domain.entity.AdminAccountAiCredit
import com.milta.feature.aicredit.domain.entity.AdminAiCredit
import com.milta.feature.aicredit.domain.entity.AdminAiCreditHistoryEntry
import com.milta.feature.aicredit.domain.entity.AiCredit
import com.milta.feature.aicredit.domain.entity.AiCreditAction
import com.milta.feature.aicredit.domain.entity.AiCreditHistoryEntry
import com.milta.feature.aicredit.domain.entity.AiCreditOutcome
import com.milta.feature.aicredit.domain.entity.AiCreditSettings
import com.milta.feature.optimizationquota.domain.entity.AdminQuotaAccountKind
import com.milta.feature.optimizationquota.domain.entity.OptimizationAccountKind
import com.milta.feature.optimizationquota.domain.entity.OptimizationSubscriptionKind
import kotlinx.serialization.Serializable

// MIL-98 — field names match the backend's camelCase JSON (System.Text.Json default). Backend
// `decimal` amounts come as plain JSON numbers, read as Double.

@Serializable
data class AiCreditDto(
    val remainingPercent: Int,
    val isLow: Boolean = false,
    val isExhausted: Boolean = false,
    val lowThresholdPercent: Int = 0,
    val resetAt: String,
    val accountKind: OptimizationAccountKind,
    val subscriptionKind: OptimizationSubscriptionKind,
    val isManuallyAdjusted: Boolean = false,
)

@Serializable
data class AiCreditHistoryItemDto(
    val id: String,
    val occurredAt: String,
    // Strings, converted leniently in toDomain(): an unknown value never fails the whole page.
    val action: String,
    val outcome: String,
    val creditPercent: Double = 0.0,
)

/** Body of both GET and PUT `admin/usage-limits/ai`. */
@Serializable
data class AiCreditSettingsDto(
    val enterpriseTrialEur: Double,
    val enterprisePaidEur: Double,
    val driverTrialEur: Double,
    val driverPaidEur: Double,
    val usdPerEur: Double,
    val lowThresholdPercent: Int,
)

@Serializable
data class AdminAiCreditDto(
    val remainingEur: Double,
    val limitEur: Double,
    val globalLimitEur: Double? = null,
    val consumedEur: Double,
    val remainingPercent: Int,
    val remainingWeightedTokens: Long = 0,
    val isLow: Boolean = false,
    val isExhausted: Boolean = false,
    val resetAt: String,
    val subscriptionKind: OptimizationSubscriptionKind,
    val isManuallyAdjusted: Boolean = false,
    val periodStart: String? = null,
    val periodCallCount: Int = 0,
    val periodFailedOrUnreadableCount: Int = 0,
    val periodWeightedTokens: Long = 0,
)

@Serializable
data class AdminAccountAiCreditDto(
    val account: AdminAccountDto,
    val accountKind: AdminQuotaAccountKind,
    val enterpriseName: String? = null,
    val credit: AdminAiCreditDto,
)

@Serializable
data class AdminAiCreditHistoryItemDto(
    val id: String,
    val occurredAt: String,
    val action: String,
    val outcome: String,
    val inputTokens: Long = 0,
    val outputTokens: Long = 0,
    val weightedTokens: Long = 0,
    val costEur: Double = 0.0,
)

@Serializable
data class ReplaceRemainingAiCreditRequest(val remainingEur: Double)

fun AiCreditDto.toDomain() = AiCredit(
    remainingPercent = remainingPercent,
    isLow = isLow,
    isExhausted = isExhausted,
    lowThresholdPercent = lowThresholdPercent,
    resetAt = resetAt,
    accountKind = accountKind,
    subscriptionKind = subscriptionKind,
    isManuallyAdjusted = isManuallyAdjusted,
)

fun AiCreditHistoryItemDto.toDomain() = AiCreditHistoryEntry(
    id = id,
    occurredAt = occurredAt,
    action = AiCreditAction.fromApi(action),
    outcome = AiCreditOutcome.fromApi(outcome),
    creditPercent = creditPercent,
)

fun AiCreditSettingsDto.toDomain() =
    AiCreditSettings(enterpriseTrialEur, enterprisePaidEur, driverTrialEur, driverPaidEur, usdPerEur, lowThresholdPercent)

fun AiCreditSettings.toDto() =
    AiCreditSettingsDto(enterpriseTrialEur, enterprisePaidEur, driverTrialEur, driverPaidEur, usdPerEur, lowThresholdPercent)

fun AdminAiCreditDto.toDomain() = AdminAiCredit(
    remainingEur = remainingEur,
    limitEur = limitEur,
    globalLimitEur = globalLimitEur ?: limitEur,
    consumedEur = consumedEur,
    remainingPercent = remainingPercent,
    remainingWeightedTokens = remainingWeightedTokens,
    isLow = isLow,
    isExhausted = isExhausted,
    resetAt = resetAt,
    subscriptionKind = subscriptionKind,
    isManuallyAdjusted = isManuallyAdjusted,
    periodStart = periodStart,
    periodCallCount = periodCallCount,
    periodFailedOrUnreadableCount = periodFailedOrUnreadableCount,
    periodWeightedTokens = periodWeightedTokens,
)

fun AdminAccountAiCreditDto.toDomain() = AdminAccountAiCredit(
    account = account.toDomain(),
    accountKind = accountKind,
    enterpriseName = enterpriseName,
    credit = credit.toDomain(),
)

fun AdminAiCreditHistoryItemDto.toDomain() = AdminAiCreditHistoryEntry(
    id = id,
    occurredAt = occurredAt,
    action = AiCreditAction.fromApi(action),
    outcome = AiCreditOutcome.fromApi(outcome),
    inputTokens = inputTokens,
    outputTokens = outputTokens,
    weightedTokens = weightedTokens,
    costEur = costEur,
)
